import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useAuth } from "@flame/shared/auth";
import { lkWallet } from "@flame/shared/api";
import { Button, Modal } from "@flame/shared/ui";
import { formatDate } from "@flame/shared/lib";
import { formatRuble } from "@flame/shared/utils";
import type { WalletTransaction } from "@flame/shared/types";

// ── Helpers ───────────────────────────────────────────────────────────────────

const TX_LABELS: Record<string, string> = {
  topup_stars:   "Пополнение через Stars",
  monthly_debit: "Ежемесячное списание",
  admin_credit:  "Зачисление администратором",
  admin_debit:   "Списание администратором",
  refund:        "Возврат",
};

function txLabel(type: string): string {
  return TX_LABELS[type] ?? type;
}

function Skeleton({ className }: { className?: string }) {
  return <div className={clsx("animate-pulse rounded bg-border", className)} />;
}

// ── Balance strip ─────────────────────────────────────────────────────────────

function BalanceStrip({
  balance,
  onTopup,
}: {
  balance: number | undefined;
  onTopup: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-surface p-5 shadow-card">
      <div>
        <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
          Баланс
        </p>
        {balance !== undefined ? (
          <>
            <p className="text-xl font-semibold tabular-nums text-fg">
              {balance.toLocaleString("ru-RU")} YY
            </p>
            <p className="text-xs text-fg-muted">≈ {formatRuble(balance)}</p>
          </>
        ) : (
          <>
            <Skeleton className="mb-1 h-6 w-24" />
            <Skeleton className="h-3 w-16" />
          </>
        )}
      </div>
      <Button variant="primary" size="md" onClick={onTopup}>
        Пополнить
      </Button>
    </div>
  );
}

// ── Topup info modal ──────────────────────────────────────────────────────────
// Online self-topup was retired 2026-06-17. Balance is credited manually by an
// administrator, so the button explains the flow instead of hitting a dead
// (410 Gone) endpoint.

function TopupInfoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Пополнение баланса"
      description="Баланс пополняет администратор."
    >
      <p className="text-sm text-fg-muted">
        Онлайн-оплата в FlameVPN отключена. Чтобы пополнить баланс, обратитесь
        к тому, кто выдал вам доступ к сервису — администратор начислит YY на ваш
        счёт вручную.
      </p>
      <div className="mt-5 flex justify-end">
        <Button variant="primary" size="md" onClick={onClose}>
          Понятно
        </Button>
      </div>
    </Modal>
  );
}

// ── Transaction row ───────────────────────────────────────────────────────────

function TxRow({ tx }: { tx: WalletTransaction }) {
  const isCredit = tx.amount >= 0;
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      {/* Type dot */}
      <div
        className={clsx(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          isCredit
            ? "bg-[var(--color-success-muted)] text-success"
            : "bg-border text-fg-muted",
        )}
        aria-hidden
      >
        {isCredit ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 10V2M2 6l4-4 4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 2v8M10 6l-4 4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      {/* Label + date */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-fg">{txLabel(tx.type)}</p>
        <p className="text-xs text-fg-subtle">{formatDate(tx.created_at)}</p>
      </div>

      {/* Amount */}
      <span
        className={clsx(
          "shrink-0 text-sm font-medium tabular-nums",
          isCredit ? "text-success" : "text-fg-muted",
        )}
      >
        {isCredit ? "+" : ""}
        {tx.amount.toLocaleString("ru-RU")} YY
      </span>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

export function WalletPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<WalletTransaction[]>([]);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["wallet-transactions", PAGE_SIZE, cursor],
    queryFn: async () => {
      const result = await lkWallet.listTransactions(PAGE_SIZE, cursor);
      setAllItems((prev) => {
        // Avoid duplicates on re-fetch
        const ids = new Set(prev.map((t) => t.id));
        const fresh = result.items.filter((t) => !ids.has(t.id));
        return cursor ? [...prev, ...fresh] : result.items;
      });
      return result;
    },
    retry: false,
  });

  const [topupOpen, setTopupOpen] = useState(false);

  const balance = me?.balance_yy;
  const hasMore = !!data?.next_cursor;

  const loadMore = () => {
    if (data?.next_cursor) setCursor(data.next_cursor);
  };

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-5">
      {/* Back */}
      <button
        type="button"
        onClick={() => void navigate(-1)}
        className="flex w-fit items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path
            d="M9 2L4 7l5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Назад
      </button>

      <h2 className="text-lg font-semibold text-fg">Кошелёк</h2>

      {/* Balance */}
      <BalanceStrip balance={balance} onTopup={() => setTopupOpen(true)} />

      <TopupInfoModal open={topupOpen} onClose={() => setTopupOpen(false)} />

      {/* 1 YY = 1 ₽ note */}
      <p className="text-xs text-fg-subtle">
        1 YY = 1 ₽ — внутренняя валюта FlameVPN. Баланс пополняет администратор.
      </p>

      {/* Transaction list */}
      <section aria-label="История операций">
        <p className="mb-2.5 text-xs font-medium uppercase tracking-wide text-fg-muted">
          История операций
        </p>

        <div className="overflow-hidden rounded-xl bg-surface shadow-card">
          {isLoading && (
            <div className="divide-y divide-border">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="mb-1 h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          )}

          {isError && !isLoading && (
            <p className="px-4 py-8 text-center text-sm text-fg-subtle">
              История операций временно недоступна
            </p>
          )}

          {!isLoading && !isError && allItems.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-fg-subtle">
              Операций пока нет
            </p>
          )}

          {allItems.length > 0 && (
            <div className="divide-y divide-border">
              {allItems.map((tx) => (
                <TxRow key={tx.id} tx={tx} />
              ))}
            </div>
          )}
        </div>

        {/* Load more */}
        {hasMore && (
          <div className="mt-3 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={loadMore}
              loading={isFetching}
              disabled={isFetching}
            >
              Загрузить ещё
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
